import { defineStore } from 'pinia'
import { ref } from 'vue'

interface ConfirmOpts {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
}
interface NotifyOpts {
  title?: string
  message: string
}
/**
 * 批153：要使用者**輸入一段文字**的對話框（撤回版本、撤銷權利的理由…）。取代 `window.prompt`。
 * 取消回 `null`；確定回輸入值（已 trim）。`required`＝空白時確定鈕反灰。
 */
interface PromptOpts {
  title?: string
  message: string
  label?: string
  placeholder?: string
  initial?: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
  required?: boolean
  maxLength?: number
  multiline?: boolean
}

type Open =
  | (ConfirmOpts & { kind: 'confirm'; resolve: (ok: boolean) => void })
  | (NotifyOpts & { kind: 'notify'; resolve: () => void })
  | (PromptOpts & { kind: 'prompt'; resolve: (value: string | null) => void })

/** 以「取消」結束一個對話框：confirm 回 false、prompt 回 null、notify 回 resolve。 */
function settleAsCancel(d: Open): void {
  if (d.kind === 'confirm') d.resolve(false)
  else if (d.kind === 'prompt') d.resolve(null)
  else d.resolve()
}

export const useDialogsStore = defineStore('dialogs', () => {
  const current = ref<Open | null>(null)

  /**
   * 只有一個 `current`：開新對話框前**先結束舊的**（R3-3）。不然第二個會蓋掉第一個，
   * 第一個的 promise 永遠不會結束——呼叫端卡在 `await`（例如兩份清單同時載入失敗時畫面停在「載入中」）。
   */
  function show(next: Open) {
    const prev = current.value
    current.value = next
    if (prev) settleAsCancel(prev)
  }

  const confirm = (o: ConfirmOpts) =>
    new Promise<boolean>((resolve) => {
      show({ kind: 'confirm', resolve, ...o })
    })
  const notify = (o: NotifyOpts) =>
    new Promise<void>((resolve) => {
      show({ kind: 'notify', resolve, ...o })
    })
  const prompt = (o: PromptOpts) =>
    new Promise<string | null>((resolve) => {
      show({ kind: 'prompt', resolve, ...o })
    })
  const close = () => {
    current.value = null
  }

  return { current, confirm, notify, prompt, close }
})
